import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMfgApi, errMsg } from './mfgApi';
import { useToast } from '../../hooks/useToast';
import { Plus, Loader2, X } from 'lucide-react';
import { useMfgRole, MfgReadOnlyBadge } from './useMfgRole';

export default function MfgProduction() {
  const mfg = useMfgApi();
  const toast = useToast();
  const [sp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(sp.get('new') === '1');
  const [units, setUnits] = useState([]);
  const [skus, setSkus] = useState([]);
  const [orders, setOrders] = useState([]);
  const { canWrite } = useMfgRole();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [entries, u, s, o] = await Promise.all([
        mfg.get('/production-entries', { limit: 100 }),
        mfg.get('/units'), mfg.get('/skus'), mfg.get('/client-orders'),
      ]);
      setRows(entries.items || entries || []);
      setUnits((u.items || u || []).filter(x => x.is_active !== false));
      setSkus((s.items || s || []).filter(x => x.is_active !== false));
      setOrders(o.items || o || []);
    } catch { toast.show('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="mfg-production-page">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">Production Entries<MfgReadOnlyBadge /></h1>
        {canWrite && <button data-testid="mfg-new-prod-btn" onClick={() => setShowNew(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 transition-colors"><Plus size={14} /> Record Production</button>}
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="production-table">
            <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
              <th className="text-left py-2.5 px-3">Date</th><th className="text-left py-2.5 px-3">Unit</th>
              <th className="text-left py-2.5 px-3">SKU</th><th className="text-left py-2.5 px-3">Order</th>
              <th className="text-right py-2.5 px-3">Qty (pcs)</th><th className="text-center py-2.5 px-3">QA</th>
              <th className="text-left py-2.5 px-3">Shift</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={7} className="text-center py-12 text-gray-400">No production entries yet.</td></tr> : rows.map(r => (
                <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`prod-row-${r.id}`}>
                  <td className="py-2.5 px-3 text-gray-700">{r.production_date}</td>
                  <td className="py-2.5 px-3 font-medium text-gray-900">{r.unit_name || '—'}</td>
                  <td className="py-2.5 px-3 text-gray-600">{r.sku_name || '—'}</td>
                  <td className="py-2.5 px-3 text-gray-600">{r.order_no || '—'}</td>
                  <td className="py-2.5 px-3 text-right font-bold text-gray-900">{r.quantity_pieces?.toLocaleString()}</td>
                  <td className="py-2.5 px-3 text-center">{r.qa_approved ? '✅' : '⏳'}</td>
                  <td className="py-2.5 px-3 text-gray-500">{r.shift || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showNew && canWrite && <NewProductionModal units={units} skus={skus} orders={orders} mfg={mfg} toast={toast} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewProductionModal({ units, skus, orders, mfg, toast, onClose, onCreated }) {
  const [form, setForm] = useState({ unit_id: '', sku_id: '', client_order_id: '', production_date: new Date().toISOString().split('T')[0], quantity_pieces: '', qa_approved: true, shift: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Filter orders by selected SKU
  const filteredOrders = form.sku_id ? orders.filter(o => o.sku_id === form.sku_id) : orders;

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await mfg.post('/production-entries', {
        unit_id: form.unit_id, sku_id: form.sku_id, client_order_id: form.client_order_id || undefined,
        production_date: form.production_date, quantity_pieces: Number(form.quantity_pieces),
        qa_approved: form.qa_approved, shift: form.shift || undefined, notes: form.notes || undefined,
      });
      toast.show('Production recorded', 'success'); onCreated();
    } catch (e) { setError(errMsg(e)); } finally { setSaving(false); }
  };

  const valid = form.unit_id && form.sku_id && Number(form.quantity_pieces) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="new-production-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-extrabold text-gray-900">Record Production</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="new-prod-close"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <Fl label="Unit"><select data-testid="prod-unit" className="input-sm" value={form.unit_id} onChange={e => set('unit_id', e.target.value)}><option value="">Select unit...</option>{units.map(u => <option key={u.id} value={u.id}>{u.name} ({u.code})</option>)}</select></Fl>
          <Fl label="SKU"><select data-testid="prod-sku" className="input-sm" value={form.sku_id} onChange={e => set('sku_id', e.target.value)}><option value="">Select SKU...</option>{skus.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}</select></Fl>
          <Fl label="Client Order (optional)"><select data-testid="prod-order" className="input-sm" value={form.client_order_id} onChange={e => set('client_order_id', e.target.value)}><option value="">— unlinked —</option>{filteredOrders.map(o => <option key={o.id} value={o.id}>{o.order_no} — {o.client_name}</option>)}</select></Fl>
          <div className="grid grid-cols-2 gap-3">
            <Fl label="Date"><input data-testid="prod-date" type="date" className="input-sm" value={form.production_date} onChange={e => set('production_date', e.target.value)} /></Fl>
            <Fl label="Quantity (pcs)"><input data-testid="prod-qty" type="number" min="1" className="input-sm" value={form.quantity_pieces} onChange={e => set('quantity_pieces', e.target.value)} /></Fl>
          </div>
          <div className="flex items-center gap-3">
            <Fl label="Shift"><input data-testid="prod-shift" className="input-sm" value={form.shift} onChange={e => set('shift', e.target.value)} placeholder="A/B/C" /></Fl>
            <label className="flex items-center gap-2 mt-4 cursor-pointer"><input type="checkbox" checked={form.qa_approved} onChange={e => set('qa_approved', e.target.checked)} data-testid="prod-qa" className="w-4 h-4 accent-emerald-500" /><span className="text-xs font-semibold text-gray-600">QA Approved</span></label>
          </div>
          {error && <p className="text-red-500 text-xs font-medium" data-testid="prod-error">{error}</p>}
        </div>
        <button data-testid="prod-submit" onClick={submit} disabled={saving || !valid} className="w-full mt-4 h-11 rounded-xl bg-emerald-500 text-white font-bold text-sm hover:bg-emerald-600 disabled:opacity-50 transition-colors">
          {saving ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Record Production'}
        </button>
      </div>
    </div>
  );
}

function Fl({ label, children }) {
  return <label className="block"><span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{label}</span><div className="mt-1">{children}</div></label>;
}
